import { Box, IconButton, List, ListItem, ListItemIcon, ListItemText } from '@mui/material'
import {
  AlertTriangleIcon,
  FileCheck02Icon,
  HomeSmileIcon,
  LineChartUp01Icon,
  ListIcon,
  Menu01Icon,
  Pin01Icon,
  Settings02Icon,
  Briefcase01Icon,
  XCloseIcon,
} from '@untitled-theme/icons-react'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { localStorage } from '@/shared/utils/localStorage'

import EditISFPDataPopup from '../EditISFPDataPopup'

import type { HamburgerMenuProps } from './HamburgerMenu.types'

const HamburgerMenu = ({ sx }: HamburgerMenuProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<DOMRect | null>(null)
  const [hasReturnTo, setHasReturnTo] = useState(false)
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false)
  const iconButtonRef = useRef<HTMLButtonElement>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { buildingUuid } = useParams<{ buildingUuid: string }>()

  useEffect(() => {
    const returnTo = localStorage.getReturnTo()
    setHasReturnTo(!!returnTo)
  }, [])

  const toggleMenu = () => {
    if (!isOpen && iconButtonRef.current) {
      const rect = iconButtonRef.current.getBoundingClientRect()
      setMenuPosition(rect)
    }
    setIsOpen(!isOpen)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleNavigate = (path: string) => {
    navigate(path)
    handleClose()
  }

  const handleEndConsultation = () => {
    const returnTo = localStorage.getReturnTo()

    if (returnTo && buildingUuid) {
      const url = returnTo.replace(':buildingUuid', buildingUuid)
      window.location.href = url
    }

    handleClose()
  }

  const handleEditData = () => {
    setIsEditPopupOpen(true)
    handleClose()
  }

  const menuItems = [
    {
      text: 'Inhaltsübersicht',
      icon: <Pin01Icon />,
      path: `/${buildingUuid}/`,
      onClick: () => handleNavigate(`/${buildingUuid}/`),
    },
    {
      text: 'Ausblick & Förderung',
      icon: <LineChartUp01Icon />,
      path: `/${buildingUuid}/energy-cost`,
      onClick: () => handleNavigate(`/${buildingUuid}/energy-cost`),
    },
    {
      text: 'Bewertung',
      icon: <HomeSmileIcon />,
      path: `/${buildingUuid}/current-rating`,
      onClick: () => handleNavigate(`/${buildingUuid}/current-rating`),
    },
    {
      text: 'Maßnahmen & Pakete',
      icon: <ListIcon />,
      path: `/${buildingUuid}/measures-overview`,
      onClick: () => handleNavigate(`/${buildingUuid}/measures-overview`),
    },
  ]

  const bottomMenuItems = [
    { text: 'Daten bearbeiten', icon: <Settings02Icon />, onClick: handleEditData },
    {
      text: 'BAFA Angaben',
      icon: <FileCheck02Icon />,
      path: `/${buildingUuid}/bafa-details`,
      onClick: () => handleNavigate(`/${buildingUuid}/bafa-details`),
    },
    {
      text: 'Enter Zusatzleistungen',
      icon: <Briefcase01Icon />,
      path: `/${buildingUuid}/additional-services`,
      onClick: () => handleNavigate(`/${buildingUuid}/additional-services`),
    },
    ...(hasReturnTo
      ? [
          {
            text: 'Energieberatung beenden',
            icon: <AlertTriangleIcon />,
            onClick: handleEndConsultation,
            danger: true,
          },
        ]
      : []),
  ]

  return (
    <>
      <IconButton
        ref={iconButtonRef}
        onClick={toggleMenu}
        sx={{
          zIndex: 1000,
          ...sx,
        }}
      >
        <Menu01Icon />
      </IconButton>

      {isOpen && (
        <Box
          onClick={handleClose}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1100,
          }}
        />
      )}

      <Box
        sx={{
          position: 'fixed',
          top: menuPosition ? menuPosition.top - 16 : 0,
          left: menuPosition ? menuPosition.left - 16 : 0,
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
          padding: '16px',
          zIndex: 1200,
          pointerEvents: isOpen ? 'auto' : 'none',
          opacity: isOpen ? 1 : 0,
          transition: 'opacity 300ms ease-in-out',
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            marginBottom: '8px',
          }}
        >
          <XCloseIcon />
        </IconButton>

        <List sx={{ padding: 0 }}>
          {menuItems.map(item => (
            <ListItem
              key={item.text}
              onClick={item.onClick}
              sx={{
                backgroundColor: item.path === location.pathname ? '#F5F5F5' : 'transparent',
                borderRadius: '12px',
                marginBottom: '2px',
                height: '56px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 'auto',
                  marginRight: '12px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  border: '1px solid #F0F0F0',
                  '& svg': {
                    fontSize: '24px',
                  },
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                slotProps={{
                  primary: {
                    fontSize: '15.6px',
                    fontWeight: 500,
                    lineHeight: '24px',
                    letterSpacing: '-0.13px',
                  },
                }}
              />
            </ListItem>
          ))}

          <Box sx={{ borderTop: '1px solid #E0E0E0', marginY: 2 }} />

          {bottomMenuItems.map(item => (
            <ListItem
              key={item.text}
              onClick={item.onClick}
              sx={{
                backgroundColor:
                  'path' in item && item.path === location.pathname ? '#F5F5F5' : 'transparent',
                borderRadius: '12px',
                marginBottom: '2px',
                height: '56px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 'auto',
                  marginRight: '12px',
                  color: item.danger ? '#740000' : undefined,
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  border: item.danger ? '1px solid #F9DEDC' : '1px solid #F0F0F0',
                  '& svg': {
                    fontSize: '24px',
                  },
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                slotProps={{
                  primary: {
                    fontSize: '15.6px',
                    fontWeight: 500,
                    lineHeight: '24px',
                    letterSpacing: '-0.13px',
                    color: item.danger ? '#740000' : undefined,
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Edit ISFP Data Popup */}
      <EditISFPDataPopup open={isEditPopupOpen} onClose={() => setIsEditPopupOpen(false)} />
    </>
  )
}

export default HamburgerMenu
